import { call, put, takeEvery } from 'redux-saga/effects';
import {
  CASE_ACCEPT_CASE_TAG_SUGGESTION_BEGIN,
  CASE_ACCEPT_CASE_TAG_SUGGESTION_SUCCESS,
  CASE_ACCEPT_CASE_TAG_SUGGESTION_FAILURE,
  CASE_ACCEPT_CASE_TAG_SUGGESTION_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { addItemImmutable } from 'utils/arrays';

export const acceptCaseTagSuggestion = createPromiseAction(CASE_ACCEPT_CASE_TAG_SUGGESTION_BEGIN);

export function dismissAcceptCaseTagSuggestionFeedback() {
  return {
    type: CASE_ACCEPT_CASE_TAG_SUGGESTION_DISMISS_FEEDBACK,
  };
}

export function* doAcceptCaseTagSuggestion(action) {
  const { caseId, suggestionName, name, categoryId } = action.payload;
  const res = yield call(api.post, `/cases/${caseId}/tags/suggestions/accept`, {
    suggestionName,
    ...(name ? { name } : {}),
    categoryId,
  });

  if (res && res.error) {
    yield put({
      type: CASE_ACCEPT_CASE_TAG_SUGGESTION_FAILURE,
      feedback: {
        message: 'feedback.acceptCaseTagSuggestionFailure',
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_ACCEPT_CASE_TAG_SUGGESTION_SUCCESS,
    data: {
      suggestionName,
      tag: res,
    },
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchAcceptCaseTagSuggestion() {
  yield takeEvery(acceptCaseTagSuggestion, withCurrentCaseId(doAcceptCaseTagSuggestion));
}

export function useAcceptCaseTagSuggestion() {
  const dispatch = useDispatch();

  const boundAction = useCallback(
    (...args) => {
      return dispatch(acceptCaseTagSuggestion(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissAcceptCaseTagSuggestionFeedback());
  }, [dispatch]);

  return {
    acceptCaseTagSuggestion: boundAction,
    dismissAcceptCaseTagSuggestionFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_ACCEPT_CASE_TAG_SUGGESTION_BEGIN + '.TRIGGER':
      return {
        ...state,
        acceptCaseTagSuggestionPending: true,
        acceptCaseTagSuggestionFeedback: null,
        acceptCaseTagSuggestionTarget: action.payload?.suggestionName || null,
      };

    case CASE_ACCEPT_CASE_TAG_SUGGESTION_SUCCESS:
      return {
        ...state,
        acceptCaseTagSuggestionPending: false,
        acceptCaseTagSuggestionFeedback: action.feedback,
        acceptCaseTagSuggestionTarget: null,
        caseTagSuggestions: Array.isArray(state.caseTagSuggestions)
          ? state.caseTagSuggestions.filter(
              (suggestion) => suggestion.name !== action.data.suggestionName,
            )
          : state.caseTagSuggestions,
        ...(state.tags && { tags: addItemImmutable(state.tags, action.data.tag) }),
      };

    case CASE_ACCEPT_CASE_TAG_SUGGESTION_FAILURE:
      return {
        ...state,
        acceptCaseTagSuggestionPending: false,
        acceptCaseTagSuggestionFeedback: action.feedback,
        acceptCaseTagSuggestionTarget: null,
      };

    case CASE_ACCEPT_CASE_TAG_SUGGESTION_DISMISS_FEEDBACK:
      return {
        ...state,
        acceptCaseTagSuggestionFeedback: null,
      };

    default:
      return state;
  }
}
